import { Link, createFileRoute } from "@tanstack/react-router";
import { useRole } from "@/hooks/use-role";
import { PermissionDeniedPage } from "@/components/team/PermissionDeniedPage";
import { AlertTriangle, Loader2 } from "lucide-react";
import { EmailCreateFlow } from "@/components/email/EmailCreateFlow";
import type { EmailCreateFlowInitialData } from "@/components/email/EmailCreateFlow";
import { useEmailSettings } from "@/hooks/use-email-settings";
import { useSession } from "@/hooks/use-session";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";

interface NewEmailSearch {
	// PRD-43 Phase 3 — pre-seed the composer audience when launched from the
	// volunteers page. `contacts` (the volunteers bulk-action) scopes to the
	// exact selection as individual contacts; `volunteers` seeds an
	// all-volunteers audience the candidate can then narrow.
	volunteers?: boolean;
	contacts?: number[];
}

function parseContactIds(raw: unknown): number[] | undefined {
	const values = Array.isArray(raw)
		? raw
		: typeof raw === "string" && raw
			? raw.split(",")
			: [];
	const ids = values
		.map((v) => Number(v))
		.filter((n) => Number.isInteger(n) && n > 0);
	return ids.length > 0 ? ids : undefined;
}

export const Route = createFileRoute("/_app/_/contacts/email/new")({
	validateSearch: (search: Record<string, unknown>): NewEmailSearch => ({
		volunteers: search.volunteers === true || search.volunteers === "true",
		contacts: parseContactIds(search.contacts),
	}),
	component: NewEmailPage,
});

function NewEmailPage() {
	const { isReadWrite } = useRole();
	if (!isReadWrite) {
		return (
			<PermissionDeniedPage
				requiredRole="Read/Write"
				message="You need Read/Write or Admin access to create an email campaign."
			/>
		);
	}
	return <NewEmailPageInner />;
}

function NewEmailPageInner() {
	const { volunteers, contacts } = Route.useSearch();
	const session = useSession();
	const campaignId = session.campaign?.id;
	const { data: emailSettings, isLoading: settingsLoading } =
		useEmailSettings(campaignId);

	// Fail-open: only render the setup gate when we explicitly know the
	// sender is unprovisioned. If the settings query errored, `data` stays
	// `undefined` and we fall through to the wizard — the backend still
	// gates create_campaign with a 400, so a transient 5xx won't lock a
	// valid user out of starting an email.
	const senderNotProvisioned = emailSettings?.is_provisioned === false;

	let initialData: EmailCreateFlowInitialData | undefined;
	if (contacts?.length) {
		initialData = {
			audiences: contacts.map((id) => ({ type: "individual", contact_id: id })),
		};
	} else if (volunteers) {
		initialData = { audiences: [{ type: "volunteer", volunteer_filter: null }] };
	}

	return (
		<div className="min-h-screen bg-muted/30 p-6">
			<div className="mx-auto max-w-3xl space-y-6">
				{settingsLoading ? (
					<div className="flex items-center justify-center py-12">
						<Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
					</div>
				) : senderNotProvisioned ? (
					<Card>
						<CardHeader>
							<CardTitle>Email not set up</CardTitle>
						</CardHeader>
						<CardContent className="space-y-4">
							<Alert>
								<AlertTriangle className="h-4 w-4" />
								<AlertDescription>
									You need a sender identity before creating an
									email campaign. Set up your from name, reply-to,
									and political disclaimer in Email Settings.
								</AlertDescription>
							</Alert>
							<div className="flex justify-end">
								<Button asChild>
									<Link to="/email-settings">
										Go to Email Settings
									</Link>
								</Button>
							</div>
						</CardContent>
					</Card>
				) : (
					<EmailCreateFlow initialData={initialData} />
				)}
			</div>
		</div>
	);
}
